import { useState, useEffect } from "react";
import { Transaction, TransactionCategory, UserProfile } from "../../types/budgethelper";
import { EPSILON } from "../constants";

interface TransactionModalProps {
  isOpen: boolean;
  editingTransaction: Transaction | null;
  transactionAmount: string;
  setTransactionAmount: (value: string) => void;
  shopName: string;
  setShopName: (value: string) => void;
  selectedCategory: TransactionCategory;
  setSelectedCategory: (value: TransactionCategory) => void;
  paidBy: string;
  setPaidBy: (value: string) => void;
  selectedBudgetGroupId: string;
  setSelectedBudgetGroupId: (value: string) => void;
  splitAmounts: { [userId: string]: number | string };
  setSplitAmounts: (value: { [userId: string]: number | string } | ((prev: { [userId: string]: number | string }) => { [userId: string]: number | string })) => void;
  budgetGroupUsers: { [groupId: string]: UserProfile[] };
  budgetGroups: any[];
  userProfileUserId: string;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
}

export const TransactionModal = ({
  isOpen,
  editingTransaction,
  transactionAmount,
  setTransactionAmount,
  shopName,
  setShopName,
  selectedCategory,
  setSelectedCategory,
  paidBy,
  setPaidBy,
  selectedBudgetGroupId,
  setSelectedBudgetGroupId,
  splitAmounts,
  setSplitAmounts,
  budgetGroupUsers,
  budgetGroups,
  userProfileUserId,
  onSubmit,
  onClose
}: TransactionModalProps) => {
  const [splitExpanded, setSplitExpanded] = useState(false);

  const amount = parseFloat(transactionAmount) || 0;
  const splitTotal = Object.values(splitAmounts).reduce((sum: number, val) => {
    const numVal = typeof val === 'string' ? parseFloat(val) || 0 : val;
    return sum + numVal;
  }, 0);

  const isFormValid = amount > 0 && shopName.trim() && Math.abs(splitTotal - amount) < EPSILON;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }

      const target = e.target as HTMLElement;
      const modalContent = document.querySelector('.modal-content');
      
      // Check if the target is inside the modal content for Enter key
      if (modalContent && modalContent.contains(target)) {
        if (e.key === 'Enter' && isFormValid) {
          // Don't trigger if user is clicking a button or in a textarea
          if (target.tagName !== 'TEXTAREA' && target.tagName !== 'BUTTON') {
            e.preventDefault();
            e.stopPropagation();
            const form = modalContent.querySelector('form') as HTMLFormElement;
            if (form) {
              form.requestSubmit();
            }
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown, true);
      return () => document.removeEventListener('keydown', handleKeyDown, true);
    }
  }, [isOpen, isFormValid, onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{editingTransaction ? "Edit Transaction" : "Add Transaction"}</h2>
        <form onSubmit={onSubmit}>
          <div className="form-group">
            <label>Amount</label>
            <input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={transactionAmount}
              required
              onChange={(e) => setTransactionAmount(e.target.value)}
              onBlur={(e) => {
                const value = parseFloat(e.target.value);
                if (!isNaN(value)) {
                  setTransactionAmount(value.toFixed(2));
                  
                  // Update split amounts when total amount changes
                  setSplitAmounts((prevSplits) => {
                    const users = budgetGroupUsers[selectedBudgetGroupId] || [];
                    
                    // If no splits exist yet, initialize with paidBy at 100%
                    if (Object.keys(prevSplits).length === 0) {
                      const newSplits: { [userId: string]: number | string } = {};
                      users.forEach((u) => {
                        newSplits[u.userId] = u.userId === paidBy ? value : 0;
                      });
                      return newSplits;
                    }
                    
                    // Reset existing splits: set paidBy to 100%, others to 0
                    const newSplits: { [userId: string]: number | string } = {};
                    Object.keys(prevSplits).forEach((userId) => {
                      newSplits[userId] = userId === paidBy ? value : 0;
                    });
                    
                    return newSplits;
                  });
                }
              }}
            />
          </div>
          <div className="form-group">
            <label>Shop Name</label>
            <input
              type="text"
              placeholder="Enter shop name"
              value={shopName}
              required
              onChange={(e) => setShopName(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as TransactionCategory)}
            >
              {Object.values(TransactionCategory).map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Paid By</label>
            <select
              value={paidBy}
              onChange={(e) => {
                const newPaidBy = e.target.value;
                setPaidBy(newPaidBy);
                
                // Update split amounts: set new paidBy to 100%, others to 0
                const amount = parseFloat(transactionAmount) || 0;
                const users = budgetGroupUsers[selectedBudgetGroupId] || [];
                const newSplits: { [userId: string]: number | string } = {};
                
                users.forEach((u) => {
                  newSplits[u.userId] = u.userId === newPaidBy ? amount : 0;
                });
                
                setSplitAmounts(newSplits);
              }}
              required
            >
              {(budgetGroupUsers[selectedBudgetGroupId] || []).map((user) => (
                <option key={user.userId} value={user.userId}>
                  {user.friendlyName || user.userId}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Budget Group</label>
            <select
              value={selectedBudgetGroupId}
              onChange={(e) => {
                setSelectedBudgetGroupId(e.target.value);
                setPaidBy(userProfileUserId);
              }}
              required
            >
              {budgetGroups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.friendlyName}
                </option>
              ))}
            </select>
          </div>

          {/* Split Bill Section */}
          {(budgetGroupUsers[selectedBudgetGroupId] || []).length > 1 && (
            <div className="form-group split-section">
              <button
                type="button"
                className="split-toggle"
                onClick={(e) => {
                  e.preventDefault();
                  setSplitExpanded(!splitExpanded);
                }}
              >
                <span>Split Bill</span>
                <span className={`toggle-icon ${splitExpanded ? 'expanded' : ''}`}>▼</span>
              </button>
              
              {splitExpanded && (
                <>
                  <div className="split-actions">
                    <button
                      type="button"
                      className="btn-link"
                      onClick={(e) => {
                        e.preventDefault();
                        const amount = parseFloat(transactionAmount) || 0;
                        const users = budgetGroupUsers[selectedBudgetGroupId] || [];
                        if (users.length > 0 && amount > 0) {
                          const totalCents = Math.round(amount * 100);
                          const baseCents = Math.floor(totalCents / users.length);
                          const leftoverCents = totalCents % users.length;
                          
                          const newSplits: { [userId: string]: number } = {};
                          users.forEach((user, index) => {
                            if (index === 0) {
                              newSplits[user.userId] = (baseCents + leftoverCents) / 100;
                            } else {
                              newSplits[user.userId] = baseCents / 100;
                            }
                          });
                          setSplitAmounts(newSplits);
                        }
                      }}
                    >
                      Split Equally
                    </button>
                    <button
                      type="button"
                      className="btn-link"
                      onClick={(e) => {
                        e.preventDefault();
                        const users = budgetGroupUsers[selectedBudgetGroupId] || [];
                        const newSplits: { [userId: string]: number | string } = {};
                        
                        // Set all users to 0
                        users.forEach((u) => {
                          newSplits[u.userId] = 0;
                        });
                        
                        setSplitAmounts(newSplits);
                      }}
                    >
                      Clear
                    </button>
                  </div>
                  
                  <div className="split-inputs">
                    {(budgetGroupUsers[selectedBudgetGroupId] || []).map((user) => {
                      const rawValue = splitAmounts[user.userId];
                      const inputValue = rawValue !== undefined 
                        ? (typeof rawValue === 'number' ? rawValue.toFixed(2) : String(rawValue))
                        : "";
                      
                      return (
                        <div key={user.userId} className="split-input-row">
                          <label>{user.friendlyName || user.userId}</label>
                          <div className="split-input-wrapper">
                            <span className="input-currency">$</span>
                            <input
                              type="text"
                              placeholder="0.00"
                              value={inputValue}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
                                  const newSplits = { ...splitAmounts, [user.userId]: value };
                                  setSplitAmounts(newSplits);
                                }
                              }}
                              onBlur={(e) => {
                                const value = parseFloat(e.target.value);
                                if (!isNaN(value) && value >= 0) {
                                  const currentAmount = parseFloat(transactionAmount) || 0;
                                  const clampedValue = Math.min(value, currentAmount);
                                  const newSplits = { ...splitAmounts, [user.userId]: parseFloat(clampedValue.toFixed(2)) };
                                
                                  const users = budgetGroupUsers[selectedBudgetGroupId] || [];
                                  if (users.length === 2) {
                                    const otherUser = users.find(u => u.userId !== user.userId);
                                    if (otherUser) {
                                      const remaining = Math.max(0, currentAmount - clampedValue);
                                      newSplits[otherUser.userId] = parseFloat(remaining.toFixed(2));
                                    }
                                  }
                                  
                                  setSplitAmounts(newSplits);
                                } else {
                                  setSplitAmounts({ ...splitAmounts, [user.userId]: 0 });
                                }
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            className="btn-max"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const currentAmount = parseFloat(transactionAmount) || 0;
                              const users = budgetGroupUsers[selectedBudgetGroupId] || [];
                              
                              const newSplits: { [userId: string]: number | string } = {};
                              users.forEach((u) => {
                                newSplits[u.userId] = u.userId === user.userId ? parseFloat(currentAmount.toFixed(2)) : 0;
                              });
                              
                              setSplitAmounts(newSplits);
                            }}
                          >
                            Max
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="split-total">
                    <span>Total:</span>
                    <span className={
                      Math.abs(splitTotal - amount) < EPSILON
                        ? "valid-split"
                        : "invalid-split"
                    }>
                      ${splitTotal.toFixed(2)} / ${amount.toFixed(2)}
                    </span>
                  </div>
                  {Math.abs(splitTotal - amount) >= EPSILON && (
                    <div className="split-error">
                      Split amounts must add up to total amount
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={!isFormValid}
            >
              {editingTransaction ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
